import type { RouteObject } from 'react-router'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'

import {
  createRouteModeAnchor,
  createVisitSiteAnchor,
} from '../client/components/Admin/TopNavBar/modes/index.js'
import { routeCrumb } from '../client/components/Admin/TopNavBar/utils/routeCrumb.js'

export const designRoutes: RouteObject = {
  path: adminRoutes.design.root,
  async lazy() {
    const { DesignMode, DesignModeBreadCrumb } = await import(
      '../client/components/Admin/Modes/DesignMode.js'
    )

    return {
      Component: DesignMode,
      handle: routeCrumb(DesignModeBreadCrumb),
    }
  },
  children: [
    {
      index: true,
      async lazy() {
        const { DesignRoute } = await import(
          '../client/components/Admin/Design/DesignRoute.js'
        )

        return { Component: DesignRoute }
      },
    },
    {
      path: 'create-space/:templateId?',
      async lazy() {
        const { CreateSpaceRoute, CreateSpaceBreadCrumb } = await import(
          '../client/components/Admin/Site/CreateSpaceRoute.js'
        )

        return {
          Component: CreateSpaceRoute,
          handle: routeCrumb(CreateSpaceBreadCrumb),
        }
      },
    },
    {
      path: 'customizer',
      async lazy() {
        const {
          CustomizerRootRoute,
          CustomizerRootRouteBreadCrumb,
          CustomizerAnchorToDesign,
        } = await import(
          '../client/components/Admin/Design/Customizer/CustomizerRootRoute.js'
        )

        return {
          Component: CustomizerRootRoute,
          handle: {
            ...routeCrumb(CustomizerRootRouteBreadCrumb),
            ...createRouteModeAnchor({
              admin: CustomizerAnchorToDesign,
            }),
          },
        }
      },
      children: [
        {
          path: 'spaces/:id/blocks',
          async lazy() {
            const {
              CustomizerSpaceRoute,
              CustomizerSpaceBreadCrumb,
              CustomizerSpaceAnchorToAdmin,
              CustomizerSpaceAnchorToModeration,
              CustomizerSpaceAnchorToVisitSite,
            } = await import(
              '../client/components/Admin/Design/Customizer/CustomizerSpaceRoute.js'
            )

            return {
              Component: CustomizerSpaceRoute,
              handle: {
                ...routeCrumb(CustomizerSpaceBreadCrumb),
                ...createRouteModeAnchor({
                  admin: CustomizerSpaceAnchorToAdmin,
                  moderation: CustomizerSpaceAnchorToModeration,
                }),
                ...createVisitSiteAnchor(CustomizerSpaceAnchorToVisitSite),
              },
            }
          },
        },
        {
          path: 'cms/:postTypeId/blocks',
          async lazy() {
            const {
              PostTypeCustomizeRoute,
              PostTypeCustomizeBreadCrumb,
              PostTypeCustomizeAnchorToAdmin,
            } = await import(
              '../client/components/Admin/Design/Customizer/PostTypeCustomizeRoute.js'
            )

            return {
              Component: PostTypeCustomizeRoute,
              handle: {
                ...routeCrumb(PostTypeCustomizeBreadCrumb),
                ...createRouteModeAnchor({
                  admin: PostTypeCustomizeAnchorToAdmin,
                }),
              },
            }
          },
        },
      ],
    },
    {
      path: 'cms',
      async lazy() {
        const { CMSIndexRoute } = await import(
          '../client/components/Admin/Design/CMS/CMSIndexRoute.js'
        )

        return { Component: CMSIndexRoute }
      },
    },
    {
      path: 'templates',
      async lazy() {
        const { TemplatesRouteGuard } = await import(
          '../client/components/Admin/Design/Templates/TemplatesRouteGuard.js'
        )

        return { Component: TemplatesRouteGuard }
      },
      children: [
        {
          index: true,
          async lazy() {
            const { TemplatesListRoute, TemplatesListBreadCrumb } =
              await import(
                '../client/components/Admin/Design/Templates/TemplatesListRoute.js'
              )

            return {
              Component: TemplatesListRoute,
              handle: routeCrumb(TemplatesListBreadCrumb),
            }
          },
        },
        {
          path: 'new',
          async lazy() {
            const { TemplateFormRoute, TemplateFormBreadCrumb } = await import(
              '../client/components/Admin/Design/Templates/TemplateFormRoute.js'
            )

            return {
              Component: TemplateFormRoute,
              handle: routeCrumb(TemplateFormBreadCrumb),
            }
          },
        },
        {
          path: 'edit/:templateId',
          async lazy() {
            const { TemplateFormRoute, TemplateFormBreadCrumb } = await import(
              '../client/components/Admin/Design/Templates/TemplateFormRoute.js'
            )

            return {
              Component: TemplateFormRoute,
              handle: routeCrumb(TemplateFormBreadCrumb),
            }
          },
        },
      ],
    },
    {
      path: 'navigation',
      async lazy() {
        const { NavigationRoute, NavigationBreadCrumb } = await import(
          '../client/components/Admin/Design/Navigation/NavigationRoute.js'
        )

        return {
          Component: NavigationRoute,
          handle: routeCrumb(NavigationBreadCrumb),
        }
      },
    },
    {
      path: 'appearance',
      async lazy() {
        const { AppearanceIndexRoute: AppearanceRootRoute } = await import(
          '../client/components/Admin/Design/Appearance/AppearanceIndexRoute.js'
        )
        const { AppearanceBreadCrumb } = await import(
          '../client/components/Admin/Design/Appearance/AppearanceRootRoute.js'
        )

        return {
          Component: AppearanceRootRoute,
          handle: routeCrumb(AppearanceBreadCrumb),
        }
      },
    },
    {
      path: 'appearance',
      async lazy() {
        const { AppearanceBreadCrumb, AppearanceRootRoute } = await import(
          '../client/components/Admin/Design/Appearance/AppearanceRootRoute.js'
        )

        return {
          Component: AppearanceRootRoute,
          handle: routeCrumb(AppearanceBreadCrumb),
        }
      },
      children: [
        {
          path: 'logos',
          async lazy() {
            const { AppearanceLogosRoute, AppearanceLogosBreadCrumb } =
              await import(
                '../client/components/Admin/Design/Appearance/AppearanceLogosRoute.js'
              )

            return {
              Component: AppearanceLogosRoute,
              handle: routeCrumb(AppearanceLogosBreadCrumb),
            }
          },
        },
        {
          path: 'colors',
          async lazy() {
            const { AppearanceColorsRoute, AppearanceColorsBreadCrumb } =
              await import(
                '../client/components/Admin/Design/Appearance/AppearanceColorsRoute.js'
              )

            return {
              Component: AppearanceColorsRoute,
              handle: routeCrumb(AppearanceColorsBreadCrumb),
            }
          },
        },
        {
          path: 'typography',
          async lazy() {
            const {
              AppearanceTypographyRoute,
              AppearanceTypographyBreadCrumb,
            } = await import(
              '../client/components/Admin/Design/Appearance/AppearanceTypographyRoute.js'
            )

            return {
              Component: AppearanceTypographyRoute,
              handle: routeCrumb(AppearanceTypographyBreadCrumb),
            }
          },
        },
        {
          path: 'styles',
          async lazy() {
            const { AppearanceStylesRoute, AppearanceStylesBreadCrumb } =
              await import(
                '../client/components/Admin/Design/Appearance/AppearanceStylesRoute.js'
              )

            return {
              Component: AppearanceStylesRoute,
              handle: routeCrumb(AppearanceStylesBreadCrumb),
            }
          },
        },
      ],
    },
  ],
}
